import React, { useEffect, useMemo } from 'react';
import { Instagram, Facebook } from 'lucide-react';
import { useMetaMessagingStore } from '@/lib/stores/metaMessagingStore';
import { useWhatsAppDrawerStore } from '@/lib/stores/whatsappDrawerStore';
import { useClientStore } from '@/lib/stores/clientStore';
import { useMetaChannels } from '@/lib/hooks/useMetaChannels';
import { MetaConversationList } from './MetaConversationList';
import { MetaThread } from './MetaThread';
import { MetaUnavailableState } from './MetaUnavailableState';
import { CHANNEL_LABEL } from './metaConversationHelpers';

interface MetaInboxProps {
  channel: 'instagram' | 'messenger';
  compact?: boolean;
  /** Chiude il drawer (per i pulsanti che portano alle impostazioni). */
  onClose?: () => void;
}

/**
 * Inbox Instagram / Messenger dentro il drawer messaggi: lista a sinistra,
 * thread a destra, stesse larghezze del layout WhatsApp. Conversazioni e realtime
 * li carica il drawer (WhatsAppDrawer), che resta montato anche da chiuso.
 */
export function MetaInbox({ channel, compact = false, onClose }: MetaInboxProps) {
  const { available, reason, loading: configLoading, canSend, canManage, conversationsByChannel } = useMetaChannels();
  const loading = useMetaMessagingStore((s) => s.loading);
  const selectedId = useMetaMessagingStore((s) => s.selectedConversationId);
  const selectConversation = useMetaMessagingStore((s) => s.selectConversation);
  const pendingConversationId = useWhatsAppDrawerStore((s) => s.pendingMetaConversationId);
  const clearPendingMetaConversation = useWhatsAppDrawerStore((s) => s.clearPendingMetaConversation);
  const clients = useClientStore((s) => s.clients);
  const leads = useClientStore((s) => s.leads);

  const conversations = conversationsByChannel[channel];

  // Apertura mirata da fuori (scheda cliente): seleziona appena la lista è arrivata.
  useEffect(() => {
    if (!pendingConversationId) return;
    if (conversations.some((c) => c.id === pendingConversationId)) {
      selectConversation(pendingConversationId);
      clearPendingMetaConversation();
    }
  }, [pendingConversationId, conversations, selectConversation, clearPendingMetaConversation]);

  // La selezione è per canale: cambiando canale il thread dell'altro non resta aperto.
  const selected = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId]
  );

  const clientNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients) map.set(c.id, c.business_name);
    for (const l of leads) if (!map.has(l.id)) map.set(l.id, l.business_name);
    return map;
  }, [clients, leads]);

  const Icon = channel === 'instagram' ? Instagram : Facebook;

  if (!available) {
    return (
      <MetaUnavailableState
        channel={channel}
        reason={configLoading ? null : reason}
        canManage={canManage}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="flex-1 flex overflow-hidden bg-[#FBFBFA] dark:bg-[#191919]">
      <div
        className={`${compact ? 'w-[370px]' : 'w-[400px]'} h-full flex-shrink-0 ${selected ? 'hidden lg:flex' : 'flex'} flex-col overflow-hidden border-r border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-[#191919]`}
      >
        <MetaConversationList
          channel={channel}
          conversations={conversations}
          selectedId={selected?.id ?? null}
          onSelect={selectConversation}
          clientNames={clientNames}
          loading={loading}
        />
      </div>
      <div className={`flex-1 h-full min-w-0 overflow-hidden ${selected ? 'flex' : 'hidden lg:flex'} flex-col`}>
        {selected ? (
          <MetaThread
            conversation={selected}
            canSend={canSend}
            linkedClientName={selected.client_id ? clientNames.get(selected.client_id) : null}
            onBack={() => selectConversation(null)}
            compact={compact}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Icon className="h-10 w-10 text-[#c9c9c5] mx-auto mb-3" />
              <p className="text-sm text-[#787774]">Scegli una conversazione {CHANNEL_LABEL[channel]}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
